import {
  ExperienceConfiguration,
  ExperienceConfigurationAnonymousResponse,
  ExperienceConfigurationAnonymousResponseSchema,
  ExperienceConfigurationRequest,
  ExperienceConfigurationResponse,
  ExperienceConfigurationResponseSchema,
} from '@monetize/types/experience-config';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { apiGet, apiPut } from '../api/axios';
import { useAuth } from '../services/auth0';

const EMPTY_EXPERIENCE_CONFIG = ExperienceConfigurationResponseSchema.parse({});

export function useGetExperienceConfiguration<
  SelectData = ExperienceConfigurationResponse,
>(
  options: Partial<
    UseQueryOptions<ExperienceConfigurationResponse, unknown, SelectData>
  > = {},
) {
  const { currentTenant } = useAuth();
  // Ensure we don't fetch unless we have a tenant
  const enabled = !!currentTenant && options.enabled !== false;

  return useQuery({
    queryKey: ['experienceConfiguration', 'authenticated'],
    queryFn: async () =>
      apiGet<ExperienceConfigurationResponse>('/experience/configuration').then(
        (res) => ExperienceConfigurationResponseSchema.parse(res.data),
      ),
    gcTime: Infinity,
    initialData: EMPTY_EXPERIENCE_CONFIG,
    meta: { showErrorToast: true },
    ...options,
    enabled,
  });
}

export function useUpdateExperienceConfiguration(
  options: Partial<
    UseMutationOptions<
      ExperienceConfiguration,
      unknown,
      ExperienceConfigurationRequest
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) =>
      apiPut<ExperienceConfiguration>('/experience/configuration', data).then(
        (res) => res.data,
      ),
    onSuccess: (...rest) => {
      queryClient.invalidateQueries({ queryKey: ['experienceConfiguration'] });
      onSuccess?.(...rest);
    },
    ...restOptions,
  });
}

export function useGetAnonymousExperienceConfiguration<
  SelectData = ExperienceConfigurationAnonymousResponse,
>(
  tenantId: string,
  options: Partial<
    UseQueryOptions<
      ExperienceConfigurationAnonymousResponse,
      unknown,
      SelectData
    >
  > = {},
) {
  return useQuery({
    queryKey: ['experienceConfiguration', 'anonymous'],
    queryFn: async () =>
      apiGet<Partial<ExperienceConfigurationAnonymousResponse>>(
        '/experience/configuration/anonymous',
        {
          axiosConfig: {
            excludeUserIdFromHeader: true,
            excludeTenantIdFromHeader: true,
            customXTenantId: tenantId,
          },
        },
      ).then((res) =>
        // Stored configs may predate newer schema fields — parsing fills in
        // defaults so consumers never see a partially-populated config.
        ExperienceConfigurationAnonymousResponseSchema.parse(res.data),
      ),
    ...options,
  });
}
